/**
 * Initial Quote Data Structure
 * Default values for a new quote configuration
 * 
 * This defines the complete data model for a quote, including:
 * - Deal information (customer, contacts, sales rep)
 * - Platform configuration (SplashBI, SplashAI, deployment)
 * - User licenses
 * - Product selections (modules, pipelines)
 * - Services
 * - Terms & conditions
 * - Discounts
 * 
 * ⭐ M13: Removed sqlConnectInd (no longer offered)
 * ⭐ M16: Added payment method and custom payment terms
 * ⭐ M18: Added multiple contact types (Primary, Billing, Influencer)
 */

// Default terms and conditions
export const defaultTerms = {
  contractTerm: '1 Year',
  startDate: '',
  endDate: '',        // ⭐ E15: End date for custom terms
  paymentTerms: 'Net 30',
  customPaymentTerms: '',  // ⭐ M16: For custom payment terms
  paymentMethod: 'Bank Wire or Check',  // ⭐ M16: Payment method
  deploymentType: '',  // ⭐ E10: Leave empty - will sync from Platform step
  instances: '1 Production, 1 Non-Production',
  vpnRequired: true,
  implementation: 'Standard functionality, Onshore/Offshore',
  training: 'Offsite/Remote',
  ratesOnshore: '$180-225/hr',
  ratesOffshore: '$55-65/hr',
  travelExpenses: 'Billed separately at actual cost',
  supportUsers: '5',
  supportTiming: 'Standard (per Support Policy)',
  supportVersions: 'Current + 1 prior version',
  managedServicesIncluded: false,
  managedServicesDetails: 'Infrastructure monitoring, upgrades, quarterly OS updates',
  customReportMaintenance: 'Not included, requires SOW',
  hyperCare: 'Time & Material basis',
  sowProvided: true
};

// Default user configuration
// ⭐ M13: Removed sqlConnectInd (no longer offered)
export const defaultUsers = {
  admin: 0,
  readOnly: 0,
  mobile: 0,
  excel: 0,
  sqlConnectEnt: 0,
  glConnect: 0
};

// Default HCM module structure
export const defaultHCMModules = {
  'Core HR': [],
  'Payroll': [],
  'Recruiting': [],
  'Benefits': [],
  'Time & Labor': [],
  'Performance Management': []
};

// Default Finance module structure
export const defaultFinanceModules = {
  'General Ledger': [],
  'Accounts Payable': [],
  'Accounts Receivable': [],
  'Cash Management': [],
  'Fixed Assets': [],
  'Expenses': []
};

// Default category discounts
export const defaultCategoryDiscounts = {
  platform: 0,
  users: 0,
  erpConnectors: 0,
  dataPipelines: 0,
  modules: 0
};

/**
 * Complete initial quote data structure
 * Used when creating a new quote
 */
export const initialQuoteData = {
  // Deal Info
  customerName: '',
  customerLegalName: '',
  customerAddress: '',
  
  // ⭐ M18: Primary/Commercial Contact
  contactName: '',
  contactPhone: '',
  contactEmail: '',
  contactTitle: '',
  
  // ⭐ M18: Billing/Contracts Contact
  billingContactName: '',
  billingContactPhone: '',
  billingContactEmail: '',
  
  // ⭐ M18: Influencer/Technical Contact
  influencerName: '',
  influencerPhone: '',
  influencerEmail: '',
  influencerRole: '',
  
  // Sales Rep
  salesRep: '',
  salesRepEmail: '',
  
  industry: '',
  region: 'US',
  country: '',        // Specific country within region
  currency: 'USD',    // ⭐ E13: Currency selection
  quoteValidityDays: 30,  // ⭐ E12: Quote validity period
  opportunityId: '',
  dealType: '',        // HCM, Finance, SCM, CRM, Cross-Domain
  employeeCount: 0,    // Required for HCM PEPM calculations
  
  // Platform
  hasSplashBI: false,
  hasSplashAI: false,
  deployment: '',

  // ERP Connectors (moved to Products)
  erpConnectors: [],
  
  // Users
  users: { ...defaultUsers },
  
  // Products
  dataPipelines: [],
  hcmModules: { ...defaultHCMModules },
  financeModules: { ...defaultFinanceModules },
  scmModules: [],
  crmModules: [],
  
  // Services (one-time)
  services: {},
  
  // ⭐ M11: Blended rate tracking
  blendedHours: 0,
  onshorePercent: 30,
  
  // Terms & Conditions
  terms: { ...defaultTerms },
  
  // Discounts
  categoryDiscounts: { ...defaultCategoryDiscounts },
  lineItemDiscounts: {}
};

/**
 * Create a fresh copy of initial quote data
 * Use this to avoid reference mutations
 * ⭐ E1 FIX: Ensures completely fresh data with no references to previous state
 * @returns {Object} New quote data object
 */
export const createEmptyQuote = () => {
  // Create deep copy to avoid any reference issues
  return JSON.parse(JSON.stringify({
    ...initialQuoteData,
    users: { ...defaultUsers },
    hcmModules: {
      'Core HR': [],
      'Payroll': [],
      'Recruiting': [],
      'Benefits': [],
      'Time & Labor': [],
      'Performance Management': []
    },
    financeModules: {
      'General Ledger': [],
      'Accounts Payable': [],
      'Accounts Receivable': [],
      'Cash Management': [],
      'Fixed Assets': [],
      'Expenses': []
    },
    scmModules: [],
    crmModules: [],
    dataPipelines: [],
    erpConnectors: [],
    services: {},
    blendedHours: 0,
    onshorePercent: 30,
    terms: { ...defaultTerms },
    categoryDiscounts: { ...defaultCategoryDiscounts },
    lineItemDiscounts: {}
  }));
};

/**
 * ⭐ E17 FIX: Industry options for deal info - expanded list
 */
export const industryOptions = [
  'Technology',
  'Healthcare',
  'Financial Services',
  'Manufacturing',
  'Retail',
  'Education',
  'Government',
  'Energy & Utilities',
  'Telecommunications',
  'Transportation & Logistics',
  'Professional Services',
  'Non-Profit',
  'Hospitality',
  'Media & Entertainment',
  'Other'
];

/**
 * ⭐ E17 FIX: Region options for deal info - includes EMEA, MENA
 */
export const regionOptions = [
  { value: 'US', label: 'United States' },
  { value: 'EMEA', label: 'Europe, Middle East & Africa' },
  { value: 'UK', label: 'United Kingdom' },
  { value: 'APAC', label: 'Asia Pacific' },
  { value: 'LATAM', label: 'Latin America' },
  { value: 'MENA', label: 'Middle East & North Africa' },
  { value: 'ANZ', label: 'Australia & New Zealand' }
];

/**
 * Country options grouped by region for granular tracking
 */
export const countryOptions = {
  US: [
    { value: 'United States', label: 'United States' },
    { value: 'Canada', label: 'Canada' }
  ],
  UK: [
    { value: 'United Kingdom', label: 'United Kingdom' },
    { value: 'Ireland', label: 'Ireland' }
  ],
  EMEA: [
    { value: 'Germany', label: 'Germany' },
    { value: 'France', label: 'France' },
    { value: 'Netherlands', label: 'Netherlands' },
    { value: 'Belgium', label: 'Belgium' },
    { value: 'Switzerland', label: 'Switzerland' },
    { value: 'Italy', label: 'Italy' },
    { value: 'Spain', label: 'Spain' },
    { value: 'Sweden', label: 'Sweden' },
    { value: 'Norway', label: 'Norway' },
    { value: 'Denmark', label: 'Denmark' },
    { value: 'Poland', label: 'Poland' },
    { value: 'South Africa', label: 'South Africa' },
    { value: 'Other EMEA', label: 'Other EMEA' }
  ],
  MENA: [
    { value: 'UAE', label: 'United Arab Emirates' },
    { value: 'Saudi Arabia', label: 'Saudi Arabia' },
    { value: 'Qatar', label: 'Qatar' },
    { value: 'Kuwait', label: 'Kuwait' },
    { value: 'Bahrain', label: 'Bahrain' },
    { value: 'Egypt', label: 'Egypt' },
    { value: 'Other MENA', label: 'Other MENA' }
  ],
  APAC: [
    { value: 'India', label: 'India' },
    { value: 'Singapore', label: 'Singapore' },
    { value: 'Japan', label: 'Japan' },
    { value: 'South Korea', label: 'South Korea' },
    { value: 'Hong Kong', label: 'Hong Kong' },
    { value: 'Malaysia', label: 'Malaysia' },
    { value: 'Thailand', label: 'Thailand' },
    { value: 'Philippines', label: 'Philippines' },
    { value: 'Indonesia', label: 'Indonesia' },
    { value: 'Other APAC', label: 'Other APAC' }
  ],
  ANZ: [
    { value: 'Australia', label: 'Australia' },
    { value: 'New Zealand', label: 'New Zealand' }
  ],
  LATAM: [
    { value: 'Mexico', label: 'Mexico' },
    { value: 'Brazil', label: 'Brazil' },
    { value: 'Argentina', label: 'Argentina' },
    { value: 'Chile', label: 'Chile' },
    { value: 'Colombia', label: 'Colombia' },
    { value: 'Other LATAM', label: 'Other LATAM' }
  ]
};

/**
 * ⭐ E13 FIX: Currency options
 */
export const currencyOptions = [
  { value: 'USD', label: 'USD ($)', symbol: '$' },
  { value: 'GBP', label: 'GBP (£)', symbol: '£' },
  { value: 'EUR', label: 'EUR (€)', symbol: '€' },
  { value: 'AED', label: 'AED', symbol: 'AED ' },
  { value: 'AUD', label: 'AUD ($)', symbol: 'A$' },
  { value: 'CAD', label: 'CAD ($)', symbol: 'C$' }
];

/**
 * Deal type / Solution area options
 */
export const dealTypeOptions = [
  { value: 'HCM', label: 'HCM (Human Capital Management)' },
  { value: 'Finance', label: 'Finance' },
  { value: 'SCM', label: 'SCM (Supply Chain Management)' },
  { value: 'CRM', label: 'CRM (Customer Relationship Management)' },
  { value: 'Cross-Domain', label: 'Cross-Domain' }
];

/**
 * Contract term options
 */
export const contractTermOptions = [
  '1 Year',
  '2 Years',
  '3 Years',
  '5 Years',
  'Custom'
];

/**
 * ⭐ M16: Payment terms options - expanded
 */
export const paymentTermsOptions = [
  { value: 'Net 30', label: 'Net 30 days' },
  { value: 'Net 45', label: 'Net 45 days' },
  { value: 'Net 60', label: 'Net 60 days' },
  { value: 'Net 90', label: 'Net 90 days' },
  { value: 'Due on Receipt', label: 'Due on Receipt' },
  { value: '50% Upfront, 50% Net 30', label: '50% Upfront, 50% Net 30' },
  { value: 'Annual Prepay', label: 'Annual Prepay (5% discount)' },
  { value: 'Quarterly', label: 'Quarterly Payments' },
  { value: 'Custom', label: 'Custom Terms' }
];

/**
 * ⭐ E12 FIX: Quote validity options (days)
 */
export const quoteValidityOptions = [
  { value: 15, label: '15 days' },
  { value: 30, label: '30 days' },
  { value: 45, label: '45 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' }
];

export default initialQuoteData;
